// Geçmiş Rapor Aktarımı — plan docs/superpowers/plans/2026-09-24-report-import.md.
// Eng review (2026-09-24) + Tasarım review (2026-09-25) + Regresyon analizi (2026-09-25)
// bu dosyanın nihai şeklini belirler (bkz. task-1-brief.md giriş blokları).
import { z } from "zod";
import { SectorCode } from "../constants/sectors.js";
import { PeriodType } from "./period.js";
import { ReportType } from "./report-type.js";

// ---- Sabitler ----
// "archived" done'dan sonra eklendi (eng review) — arşivlenmiş import kaydı.
export const REPORT_IMPORT_STATUSES = [
  "uploaded",
  "queued",
  "mapping",
  "extracting",
  "writing",
  "done",
  "archived",
  "failed",
  "cancelled",
] as const;
export const ReportImportStatus = z.enum(REPORT_IMPORT_STATUSES);
export type ReportImportStatus = z.infer<typeof ReportImportStatus>;
/** Aktif = tekillik kontrolüne giren durumlar (failed/cancelled/archived hariç). */
export const REPORT_IMPORT_ACTIVE_STATUSES: readonly ReportImportStatus[] = [
  "uploaded",
  "queued",
  "mapping",
  "extracting",
  "writing",
  "done",
];
export const REPORT_IMPORT_MAX_FILE_BYTES = 50 * 1024 * 1024;
export const REPORT_IMPORT_ALLOWED_MIMES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;
export const REPORT_IMPORT_MAX_BATCH_ROWS = 100;
// ≤15 okunabilir sayfa → LLM haritası atlanır (kısayol).
// Kalibrasyon 2026-09-26: kısayol her sayfayı her bölüme verir, çıkarım maliyeti ~7×;
// harita çağrısı ucuz (~$0,05). KMPUR (33 sayfa, 11 bölüm) sayfa başına $0,081 ile
// korpusun en pahalı raporuydu; LLM haritası kullanan 83 sayfalık Sinpaş $0,014.
export const REPORT_IMPORT_LLM_MAP_SKIP_PAGES = 15;
// Eng review (2026-09-24): "cancelled" bir failure reason DEĞİL (kullanıcı iptali status'te
// tutulur), "rate_limit" eklendi.
export const ReportImportFailureReason = z.enum([
  "no_text",
  "unsupported_file",
  "extraction_failed",
  "llm_auth",
  "rate_limit",
  "llm_error",
  "write_failed",
  "timeout_stale",
]);
export type ReportImportFailureReason = z.infer<typeof ReportImportFailureReason>;

// ---- Dosya ----
// storagePath klasörü = importFileId (bkz. ImportPresignResponse.importFileId), UUID.
export const ReportImportFileSchema = z.object({
  kind: z.enum(["pdf", "docx"]),
  storagePath: z
    .string()
    .min(1)
    .regex(/^imports\/[0-9a-f-]{36}\/[^/]+$/),
  fileName: z.string().min(1).max(255),
  sizeBytes: z.number().int().positive().max(REPORT_IMPORT_MAX_FILE_BYTES),
  pageCount: z.number().int().nonnegative().optional(),
  primary: z.boolean(),
});
export type ReportImportFile = z.infer<typeof ReportImportFileSchema>;

const FilesSchema = z
  .array(ReportImportFileSchema)
  .min(1)
  .max(2)
  .superRefine((files, ctx) => {
    const primaries = files.filter((f) => f.primary);
    if (primaries.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Tam olarak bir birincil dosya olmalı",
      });
    }
    const kinds = new Set(files.map((f) => f.kind));
    if (kinds.size !== files.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Aynı türden iki dosya olamaz" });
    }
    if (kinds.has("docx") && primaries[0]?.kind !== "docx") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Word varsa birincil dosya Word olmalı (K16)",
      });
    }
  });

// ---- Parti satırı / istekler ----
export const ImportBatchRowSchema = z.object({
  files: FilesSchema,
  clientId: z.string().uuid(),
  frameworkVersionId: z.string().uuid(),
  sectorPackageId: z.string().uuid().optional(),
  reportingYear: z.number().int().min(2000).max(2100),
  periodType: PeriodType.default("annual"),
  reportType: z.string().max(40).optional(), // serbest etiket (K14): EFR / YK FR / GRI / TSRS …
});
export type ImportBatchRow = z.infer<typeof ImportBatchRowSchema>;

export const CreateImportBatchRequestSchema = z.object({
  rows: z.array(ImportBatchRowSchema).min(1).max(REPORT_IMPORT_MAX_BATCH_ROWS),
});
export type CreateImportBatchRequest = z.infer<typeof CreateImportBatchRequestSchema>;

export const FillProjectFromReportRequestSchema = z.object({
  projectId: z.string().uuid(),
  files: FilesSchema,
});
export type FillProjectFromReportRequest = z.infer<typeof FillProjectFromReportRequestSchema>;

export const ImportPresignRequestSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(REPORT_IMPORT_ALLOWED_MIMES),
  sizeBytes: z.number().int().positive().max(REPORT_IMPORT_MAX_FILE_BYTES),
});
export type ImportPresignRequest = z.infer<typeof ImportPresignRequestSchema>;
export const ImportPresignResponseSchema = z.object({
  importFileId: z.string().uuid(),
  uploadUrl: z.string(),
  storagePath: z.string(),
  expiresIn: z.number().int(),
});
export type ImportPresignResponse = z.infer<typeof ImportPresignResponseSchema>;

// ---- Kapak önerisi / tahmin ----
export const ImportSuggestionSchema = z.object({
  clientName: z.string().nullable(),
  suggestedClientId: z.string().uuid().nullable(),
  year: z.number().int().nullable(),
  periodType: PeriodType.nullable(),
  // Kapakta yazan HAM etiket (değişmedi — prompt aynı) ...
  reportType: z.string().nullable(),
  // ... ve aynı etiketin sabit listeye indirgenmiş hali (`normalizeReportType`).
  // Satır `<select>`'i bunu kullanır; eşleşmezse null → alan boş kalır, ✦ konmaz.
  reportTypeId: ReportType.nullable(),
  /**
   * Kapaktan okunan sektör, sabit sözlüğe (`SECTOR_CODES`) indirgenmiş hali.
   * Sektör paketi önerisinde müşteri kaydının `sector` alanı ÖNCELİKLİ; bu alan
   * yalnız kayıtta sektör yoksa kullanılır. Eşleşme yoksa null (öneri yapılmaz).
   */
  sector: SectorCode.nullable().default(null),
  confidence: z.number().min(0).max(1),
  pageCount: z.number().int().nonnegative().nullable(),
});
export type ImportSuggestion = z.infer<typeof ImportSuggestionSchema>;
export const ImportSuggestRequestSchema = z.object({ storagePath: z.string().min(1) });

export const ImportEstimateRequestSchema = z.object({
  pages: z.array(z.number().int().nonnegative()).min(1).max(REPORT_IMPORT_MAX_BATCH_ROWS),
});
export const ImportEstimateSchema = z.object({
  totalPages: z.number().int(),
  processablePages: z.number().int(),
  estimatedUsd: z.number(),
  estimatedMinutes: z.number(),
  calibrated: z.boolean(),
});
export type ImportEstimate = z.infer<typeof ImportEstimateSchema>;

// ---- Eşlenmemiş içerik / istatistik ----
export const UnmappedContentItemSchema = z.object({
  title: z.string(),
  text: z.string(),
  sourceRef: z.string(), // "PDF s.12–14" | "Başlık › paragraf 4–7"
});
export type UnmappedContentItem = z.infer<typeof UnmappedContentItemSchema>;

export const ImportStatsSchema = z.object({
  answered: z.number().int().default(0),
  empty: z.number().int().default(0),
  lowConfidence: z.number().int().default(0),
  rejected: z
    .object({
      grounding: z.number().int().default(0),
      schema: z.number().int().default(0),
      /**
       * BÖLÜM ANLATISI reddi — SORU DEĞİL (inceleme). Eskiden `grounding`'e yazılıyordu
       * ve FE'de "x/y soru" toplamını şişiriyordu.
       */
      narrative: z.number().int().default(0),
    })
    .default({}),
  unmappedSections: z.array(z.string()).default([]),
  failedSections: z.array(z.string()).default([]),
  unreadablePages: z.number().int().default(0),
  excludedPages: z.number().int().default(0),
  priorYearValuesSeen: z.number().int().default(0),
  tokens: z.number().int().default(0),
  costUsd: z.number().default(0),
  sectionsWritten: z.number().int().default(0),
  // Eng review (2026-09-24) ekleri:
  trimmedSpans: z.number().int().default(0),
  // P6 (2026-09-30) — en az bir satırı/parçası reddedilmiş ama yine de yazılan yanıt sayısı.
  partialAnswers: z.number().int().default(0),
  /**
   * P12/1 (2026-09-30) — satır hücresi KOMŞU cümle penceresiyle (±2, aynı ünite)
   * doğrulanan hücre sayısı. Genişlemenin ne sıklıkta gerektiğini ölçer.
   */
  rowWindowHits: z.number().int().default(0),
  /** P12/1 — birleşik hücre yüzünden önceki satırdan kimliği devralan satır sayısı. */
  rowIdentityInherited: z.number().int().default(0),
  /** P14/1 — kolon başlığı/sayım etiketi kuralıyla kabul edilen hücre sayısı. */
  rowHeaderLabelHits: z.number().int().default(0),
  /** P14/2 — kimliğini GRUP BAŞINDAN (üstteki başlık satırı) alan satır sayısı. */
  rowIdentityFromGroupHead: z.number().int().default(0),
  /**
   * run3 K12 — ÖLÇEK SAYAÇLARI. Parti 2667'de 44 hücre yanlış büyüklükte yazıldı ve
   * `scale_factor` tek satırda dolduğu için bu YALNIZ elle PDF karşılaştırmasıyla
   * görülebildi. Üç sayaç bir sonraki partide tek sorguyla görünür kılar:
   *   - `scaleApplied`: çarpan UYGULANAN hücre sayısı,
   *   - `scaleHints`: kaynakta ölçek işareti VAR ama çarpan uygulanamadı (uyarıldı),
   *   - `unitFamilyMismatch`: sütun birimi ölçek taşırken kaynak çıplak birim beyan etti.
   * Hepsi `default(0)` — eski aktarım kayıtları olduğu gibi geçerli kalır.
   */
  scaleApplied: z.number().int().default(0),
  scaleHints: z.number().int().default(0),
  unitFamilyMismatch: z.number().int().default(0),
  /**
   * P9 (2026-09-30) — boş soru ikinci geçişi. OPSİYONEL: alan yoksa ikinci geçiş
   * hiç koşmamıştır (eski aktarım kayıtları da bu yüzden olduğu gibi geçerli kalır).
   */
  secondPass: z
    .object({
      questions: z.number().int().default(0),
      sections: z.number().int().default(0),
      calls: z.number().int().default(0),
      answered: z.number().int().default(0),
      tokens: z.number().int().default(0),
      /** Bölüm-kapsamlı hatayla düşen ikinci geçiş bölümleri (iş durmaz). */
      failed: z.array(z.string()).default([]),
      /** GERÇEKLEŞEN token tavanı dolduğu için hiç denenmeyen bölümler. */
      skippedForBudget: z.array(z.string()).default([]),
      /** İş-kapsamlı hata (rate limit / auth) geçişi erken bitirdi mi. */
      aborted: z.boolean().default(false),
      /**
       * P12/3 — kurtarılan METİN yanıtında soru etiketinin hiçbir gövdesi geçmiyordu:
       * güven 0,69'a indirildi ("Kontrol gerekli"), yanıt REDDEDİLMEDİ.
       */
      weakTopic: z.number().int().default(0),
      /**
       * P14/5 (TODO-IMPORT-15) — EŞLENMİŞ sayfalarda soru bazlı yeniden deneme:
       * `calls`/`answered` toplamlarının bu kipe düşen payı. Aynı bütçeyi (0,6) paylaşır.
       */
      mappedRetryCalls: z.number().int().default(0),
      mappedRetryAnswered: z.number().int().default(0),
    })
    .optional(),
  conflicts: z.number().int().default(0),
  /**
   * S3 (parti e290 §7.2) — çıkarımda düşürülen bozuk kod noktası sayısı. `unpdf` bazı
   * gömülü fontlarda harfleri kontrol/tahsis edilmemiş kod noktalarına eşliyor
   * ("eşit \x89ırsat"); 0'dan büyük değer o raporun yazı tipi eşlemesinin bozuk
   * olduğunu söyler.
   */
  glyphDrops: z.number().int().default(0),
  /**
   * S4 (parti e290 §7.3) — düzyazı olmadığı için güveni eşiğin altına çekilen `text`
   * yanıtı sayısı (infografik/tablo dökümü).
   */
  proseCapped: z.number().int().default(0),
  /**
   * S4 — aynı rapor içinde BİREBİR aynı metni alan ikinci (ve sonraki) `text` yanıtı:
   * reddedilir, köken `proposed_value` ile saklanır. Doluluk ölçütünü dürüstleştirir
   * (parti e290'da 14 yanıt / 5 küme mükerrerdi).
   */
  duplicateAnswers: z.number().int().default(0),
  rateLimitWaits: z.number().int().default(0),
  fallbackToPdf: z.boolean().default(false),
});
export type ImportStats = z.infer<typeof ImportStatsSchema>;

export const ImportProgressSchema = z.object({
  sectionsDone: z.number().int(),
  sectionsTotal: z.number().int(),
});
export type ImportProgress = z.infer<typeof ImportProgressSchema>;

// ---- DTO ----
export const ReportImportSchema = z.object({
  id: z.string().uuid(),
  batchId: z.string().uuid(),
  mode: z.enum(["create", "fill"]),
  clientId: z.string().uuid(),
  clientName: z.string(),
  // Eng review (2026-09-24): frameworkId eklendi — frameworkVersionId dondurulmuş sürümdür,
  // frameworkId framework'ün kendisidir (ör. liste filtresi/gruplama için).
  frameworkId: z.string().uuid(),
  frameworkVersionId: z.string().uuid(),
  frameworkName: z.string(),
  versionNumber: z.string(),
  sectorPackageId: z.string().uuid().nullable(),
  reportingYear: z.number().int(),
  periodType: PeriodType,
  reportType: z.string().nullable(),
  projectId: z.string().uuid().nullable(),
  files: z.array(ReportImportFileSchema),
  status: ReportImportStatus,
  progress: ImportProgressSchema.nullable(),
  stats: ImportStatsSchema.nullable(),
  unmappedCount: z.number().int(),
  failureReason: ReportImportFailureReason.nullable(),
  failureMessage: z.string().nullable(),
  retryCount: z.number().int(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type ReportImport = z.infer<typeof ReportImportSchema>;

export const ReportImportDetailSchema = ReportImportSchema.extend({
  unmappedContent: z.array(UnmappedContentItemSchema),
  suggestion: ImportSuggestionSchema.nullable(),
});
export type ReportImportDetail = z.infer<typeof ReportImportDetailSchema>;

// Tasarım review (2026-09-25) 9A: serbest metin arama (dosya adı / müşteri adı).
export const ReportImportsListQuerySchema = z.object({
  status: ReportImportStatus.optional(),
  clientId: z.string().uuid().optional(),
  batchId: z.string().uuid().optional(),
  finished: z.enum(["true", "false"]).optional(), // Tamamlanan sekmesi: done/failed/cancelled
  q: z.string().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ReportImportsListQuery = z.infer<typeof ReportImportsListQuerySchema>;

export const ReportImportsListResponseSchema = z.object({
  imports: z.array(ReportImportSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  queue: z.object({ running: z.number().int(), waiting: z.number().int() }),
});
export type ReportImportsListResponse = z.infer<typeof ReportImportsListResponseSchema>;

export const CreateImportBatchResponseSchema = z.object({
  batchId: z.string().uuid(),
  imports: z.array(ReportImportSchema),
});
export type CreateImportBatchResponse = z.infer<typeof CreateImportBatchResponseSchema>;

// Eng review (2026-09-24): arşivleme endpoint yanıtı.
export const ArchiveImportResponseSchema = z.object({
  import: ReportImportSchema,
});
export type ArchiveImportResponse = z.infer<typeof ArchiveImportResponseSchema>;

// ---- Köken ----
/** `rejectedRows` bu sayıda satırdan fazlasını taşımaz (köken satırı şişmesin). */
export const PARTIAL_REJECTED_ROWS_MAX = 50;
/**
 * Tek bir reddedilen satırın `proposed` payload'ı bu karakter sınırını (JSON uzunluğu)
 * aşmaz — 50 satır × sınırsız metin hücresi köken satırını şişirirdi (inceleme C6).
 * Üretici tarafında uygulanır (`verify-answer.ts` `capProposed`).
 */
export const PARTIAL_PROPOSED_MAX_CHARS = 2000;

/**
 * P6 — satır bazlı kısmi kabul (TODO-IMPORT-09). Tolerans 0 **hücre** düzeyinde kalır;
 * granülerlik satıra taşındı: tablo/repeater'da doğrulanan satırlar yazılır,
 * doğrulanamayanlar yazılmaz ve burada iz bırakır. `index` gösterime hazır bir
 * etikettir ("satır 3" | composite parça kodu | "parca · satır 2").
 */
export const PartialAcceptanceSchema = z.object({
  acceptedRows: z.number().int().nonnegative(),
  totalRows: z.number().int().nonnegative(),
  rejectedRows: z
    .array(
      z.object({
        index: z.string(),
        reason: z.enum(["grounding", "schema"]),
        detail: z.string(),
        proposed: z.unknown().nullable(),
      }),
    )
    .max(PARTIAL_REJECTED_ROWS_MAX),
  /**
   * P12 incelemesi (#3) — kimliğini ÖNCEKİ satırdan devralan satırların etiketleri
   * ("satır 3"). Kaynak tabloda birleşik hücre olduğu için satırın kimlik hücresi kendi
   * cümlesinde doğrulanamadı; iz burada durur. OPSİYONEL — eski köken satırları
   * alansız geçerlidir ve alan doluyken de FE "kısmi" rozeti YALNIZ `rejectedRows`'a
   * bakar (devir tek başına yanıtı "kısmi" yapmaz).
   */
  inheritedRows: z.array(z.string()).max(PARTIAL_REJECTED_ROWS_MAX).optional(),
});
export type PartialAcceptance = z.infer<typeof PartialAcceptanceSchema>;

export const ResponseProvenanceSchema = z
  .object({
    questionCode: z.string(),
    importId: z.string().uuid(),
    sourcePages: z.array(z.number().int().positive()).nullable(),
    sourceLocator: z.string().nullable(),
    snippet: z.string().max(300),
    confidence: z.number().min(0).max(1),
    rejected: z.boolean(),
    rejectedReason: z.enum(["grounding", "schema"]).nullable(),
    proposedValue: z.unknown().nullable(),
    // P8(c) — "ai-import-trim": değer sorunun `maxLength` sınırına indirildi.
    // P15/1b — "ai-import-scaled": kaynaktaki ölçek ("Bin TL" / "Milyon TL") okunup
    // değer ×1.000 / ×1.000.000 yazıldı; FE rozete "×1.000 ölçeklendi" ekler.
    // `response_provenance.method` varchar(16) — "ai-import-scaled" tam 16 karakter.
    method: z.enum(["ai-import", "ai-import-trim", "ai-import-scaled"]),
    /**
     * P15 inceleme (karar 2) — UYGULANAN ölçek çarpanı (1000 · 1000000); ölçek
     * yoksa null. FE rozeti/satırı bu sayıyı biçimlendirir ("×1.000.000 ölçeklendi");
     * sabit metin yazmak ×1.000.000 vakasında yanlış çarpan gösteriyordu.
     *
     * G2 (parti e290 §6.2) — çarpan artık TAM SAYI DEĞİL: ölçek ailesi dönüşümünde
     * kaynak hedeften büyük ölçekliyse oran 1'den küçüktür (Bin TL → Milyon TL =
     * 0,001) ve FE "÷1.000 ölçeklendi" yazar. Kolon `numeric(24,9)` (migration 0043).
     */
    scaleFactor: z.number().positive().nullable().default(null),
    /**
     * run2-2 (a) — AYRINTI: "neden böyle?" tek satır. Reddedilen satırda red ayrıntısı
     * (hangi hücre, hangi kural), KABUL EDİLİP güveni eşiğin altına çekilen satırda
     * tavanın sebebi ("ölçek ipucu var …, sütun birimi yok", "sayı yazımı belirsiz",
     * "konu eşleşmesi zayıf"). FE düşük güven satırında rozetin yanına yazar.
     * Null = ayrıntı yok (eski satırlar dahil).
     */
    detail: z.string().max(400).nullable().default(null),
    // P6 — satır bazlı kısmi kabul; yalnız reddedilen satır varsa dolu.
    partial: PartialAcceptanceSchema.nullable(),
  })
  .refine((p) => !p.rejected || p.rejectedReason !== null, {
    message: "Reddedilen köken için sebep zorunlu",
  });
export type ResponseProvenance = z.infer<typeof ResponseProvenanceSchema>;

// Eng review (2026-09-24, T1 eki) + Tasarım review (2026-09-25): ProjectDetail'de proje
// başına birden fazla import (create + sonraki fill'ler) gösterilir — tek `import` alanı
// yerine liste + en son import'a işaret eden ayrı id.
export const ImportSummarySchema = z.object({
  id: z.string().uuid(),
  mode: z.enum(["create", "fill"]),
  status: ReportImportStatus,
  fileName: z.string(),
  fileUrl: z.string(),
  fileKind: z.enum(["pdf", "docx"]),
  finishedAt: z.string().nullable(),
});
export type ImportSummary = z.infer<typeof ImportSummarySchema>;

export const ProjectProvenanceResponseSchema = z.object({
  imports: z.array(ImportSummarySchema),
  latestImportId: z.string().uuid().nullable(),
  entries: z.array(ResponseProvenanceSchema),
  sections: z.array(
    z.object({
      sectionCode: z.string(),
      sourcePages: z.array(z.number().int()).nullable(),
      sourceLocator: z.string().nullable(),
      sectionUpdatedAt: z.string().nullable(),
    }),
  ),
  /** Düşük güven eşiği (system_settings report_import.confidence_threshold) — FE rozet/kart kararı için. */
  threshold: z.number().min(0).max(1),
});
export type ProjectProvenanceResponse = z.infer<typeof ProjectProvenanceResponseSchema>;
